import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { CountBadge } from '@/components/admin/CountBadge';
import { BADGE_LABELS, menuFor, type BadgeKey, type MenuChild, type MenuItem } from '@/components/admin/menu';
import { themeFor } from '@/components/admin/roleTheme';
import type { Role } from '@/features/auth/api';
import { fetchEvent } from '@/features/events/api';
import { fetchEventTypes } from '@/features/masters/api';

/** その項目（または子項目）が現在地かどうか。`/members` と `/members/import` の両方を拾う */
function matchesPath(to: string, pathname: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function eventDetailId(pathname: string): string | undefined {
  const match = pathname.match(/^\/events\/([^/]+)/);
  const id = match?.[1];
  if (!id || id === 'new' || id === 'recurring' || id === 'import') return undefined;
  return id;
}

/** 子メニューが現在地か。長いパスを優先し、種別クエリはイベント詳細にも追随する（D-234） */
function childActive(
  childTo: string,
  siblings: MenuChild[],
  pathname: string,
  search: string,
  eventTypeId: string | null,
): boolean {
  const score = childMatchScore(childTo, pathname, search, eventTypeId);
  if (score < 0) return false;
  return siblings.every(
    (other) =>
      other.to === childTo || childMatchScore(other.to, pathname, search, eventTypeId) <= score,
  );
}

function childMatchScore(
  to: string,
  pathname: string,
  search: string,
  eventTypeId: string | null,
): number {
  const [path = '', query] = to.split('?');
  if (query) {
    const typeId = new URLSearchParams(query).get('type');
    if (!typeId) {
      return pathname === path && search === `?${query}` ? path.length : -1;
    }
    if (pathname === '/events' && search === `?type=${typeId}`) return 10_000;
    if (pathname === '/events/new' && new URLSearchParams(search).get('type') === typeId) {
      return 10_000;
    }
    if (eventTypeId === typeId && eventDetailId(pathname)) return 10_000;
    return -1;
  }
  if (pathname === path) return path.length + 50;
  if (pathname.startsWith(`${path}/`)) return path.length;
  return -1;
}

function activeParentLabel(items: MenuItem[], pathname: string): string | null {
  const hit = items.find(
    (item) =>
      item.children &&
      (matchesPath(item.to, pathname) ||
        item.children.some((child) => matchesPath(child.to, pathname))),
  );
  return hit?.label ?? null;
}

/**
 * 左サイドバー（幅200px・折りたたみ可 / 指示書 §7）。
 * 現在地はアクセントカラーで強調。
 * サブメニューは親をクリックすると開き、現在地を含むセクションは自動で開く（D-078）。
 * モバイル（〜782px）はハンバーガーで開閉する。
 * PCでは「メニューを畳む」で幅を詰められる（D-109）。畳むと項目名は隠れ、
 * 頭文字と件数バッジだけが残る（どこに何があったかの手がかりを消さないため）。
 *
 * 地色・アクセントは**ロールごとに変える**（D-101）。色はCSS変数で1か所に流し込み、
 * 個々の項目は `var(--role-*)` を参照する（Tailwindの動的クラス生成を避けるため）。
 */
export function Sidebar({
  role,
  open,
  onNavigate,
  badges = {},
  collapsed = false,
  onToggleCollapse,
}: {
  role: Role;
  open: boolean;
  onNavigate?: () => void;
  /** 未対応件数（キーは menu.ts の BadgeKey） */
  badges?: Partial<Record<BadgeKey, number>>;
  /** PCで幅を詰める（D-109） */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}): ReactElement {
  const { pathname, search } = useLocation();
  const theme = themeFor(role);
  const viewingEventId = eventDetailId(pathname);
  const viewingEvent = useQuery({
    queryKey: ['event', viewingEventId],
    queryFn: () => fetchEvent(viewingEventId!),
    enabled: Boolean(viewingEventId),
    staleTime: 60_000,
  });
  const eventTypeId = viewingEvent.data?.type_id ?? null;

  // イベント種別ごとの一覧をサブメニューに出す（D-184 / D-297）。種別は業務マスターなので、
  // 種別が増えてもメニューは自動で増える（システム改修は不要）。
  const typesQuery = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    enabled: role === 'office' || role === 'director' || role === 'success',
    staleTime: 5 * 60_000,
  });
  const items = useMemo(() => {
    const base = menuFor(role);
    const types = [...(typesQuery.data ?? []).filter((t) => t.is_active)];
    if (types.length === 0) return base;
    // サクセスはサクセス面談を先頭に（主導線 / D-297）。
    // 相手ロールが自分と違う面談種別は出さない（理事にサクセス面談・サクセスに1on1は出さない / D-298）
    const visible = types.filter((t) => {
      if (t.counterpart_role == null) return true;
      if (role === 'director') return t.counterpart_role === 'director';
      if (role === 'success') return t.counterpart_role === 'success';
      return true;
    });
    visible.sort((a, b) => {
      if (role === 'success') {
        const rank = (t: (typeof visible)[number]): number =>
          t.counterpart_role === 'success' ? 0 : 1;
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
      }
      return a.name.localeCompare(b.name, 'ja');
    });
    const typeChildren: MenuChild[] = visible.map((t) => ({
      label: t.name,
      to: `/events?type=${t.id}`,
    }));
    return base.map((item) => {
      if (item.label === 'イベント管理' && item.children) {
        return {
          ...item,
          // 「イベント一覧（全種別）」の子リンクは出さない（親クリックで開ける。
          // 種別ごとの一覧が主導線 / D-184・実機フィードバック）
          children: [...typeChildren, ...item.children.slice(1)],
        };
      }
      if (
        (item.label === '担当イベント' || item.label === 'イベント') &&
        item.children
      ) {
        return { ...item, children: typeChildren };
      }
      return item;
    });
  }, [role, typesQuery.data]);
  const currentSection = useMemo(() => activeParentLabel(items, pathname), [items, pathname]);
  const [expanded, setExpanded] = useState<string | null>(currentSection);

  // 子ページへ直接来たとき・別セクションへ移ったときも、そのセクションを開いて導線を見せる
  useEffect(() => {
    if (currentSection) setExpanded(currentSection);
  }, [currentSection]);

  const themeVars = {
    backgroundColor: theme.sidebar,
    '--role-hover': theme.sidebarHover,
    '--role-accent': theme.accent,
  } as CSSProperties;

  return (
    <nav
      aria-label="メインメニュー"
      style={themeVars}
      className={`block shrink-0 text-wpsidebar-text wpmobile:absolute wpmobile:inset-y-0 wpmobile:left-0 wpmobile:z-30 wpmobile:w-sidebar ${
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
      } ${
        // 782px 以下（§7: モバイル）はハンバーガーで開いたときだけ表示する
        open ? 'wpmobile:block' : 'wpmobile:hidden'
      }`}
    >
      {/* どの立場の画面かを示す帯。色だけに頼らず文字でも書く */}
      <p
        style={{ backgroundColor: theme.accent, color: theme.accentText }}
        className={`truncate py-1.5 text-xs font-semibold ${collapsed ? 'text-center' : 'px-3'}`}
        title={`${theme.label}の画面`}
      >
        {collapsed ? theme.label.slice(0, 2) : `${theme.label}の画面`}
      </p>

      {/* PCだけに出す折りたたみ切替（モバイルはアドミンバーのハンバーガーが担う / D-109） */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          title={collapsed ? 'メニューを開く' : 'メニューを畳む'}
          className={`flex w-full items-center gap-2 py-1.5 text-xs text-wpsidebar-muted hover:bg-[var(--role-hover)] wpmobile:hidden ${
            collapsed ? 'justify-center px-1' : 'px-3'
          }`}
        >
          <span aria-hidden="true">{collapsed ? '»' : '«'}</span>
          {!collapsed && <span>メニューを畳む</span>}
        </button>
      )}

      <ul className="py-1">
        {items.map((item) => (
          <li key={item.to + item.label}>
            <div className="flex items-stretch">
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={() => {
                  // 親をクリックしたら遷移と同時にサブメニューも開く（▸ を押さないと
                  // 子項目に気づけない状態だった / D-078）
                  if (item.children) setExpanded(item.label);
                  onNavigate?.();
                }}
                title={item.label}
                className={({ isActive }) =>
                  `flex min-w-0 flex-1 items-center gap-2 border-l-4 py-2.5 hover:bg-[var(--role-hover)] ${
                    collapsed ? 'justify-center px-1' : 'px-3'
                  } ${
                    isActive
                      ? 'border-[var(--role-accent)] bg-[var(--role-hover)] font-semibold'
                      : 'border-transparent'
                  }`
                }
              >
                {collapsed ? (
                  // 畳んだときは頭2文字＋バッジを重ねる（どの項目に件数が付いているか分かるように）
                  <span
                    className={`relative inline-block whitespace-nowrap py-0.5 text-xs leading-none ${
                      // バッジがある項目だけ右に逃がす（無い項目まで詰めると折り返す）
                      item.badge && (badges[item.badge] ?? 0) > 0 ? 'pr-4' : ''
                    }`}
                  >
                    {item.label.slice(0, 2)}
                    {item.badge && (badges[item.badge] ?? 0) > 0 && (
                      <CountBadge
                        count={badges[item.badge] ?? 0}
                        label={BADGE_LABELS[item.badge]}
                        className="absolute -top-1.5 right-0 scale-90"
                      />
                    )}
                  </span>
                ) : (
                  <>
                    {/* 折り返すとバッジの位置がずれるため1行に収める（あふれは …）/ D-109 */}
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                      <CountBadge
                        count={badges[item.badge] ?? 0}
                        label={BADGE_LABELS[item.badge]}
                        className="ml-auto"
                      />
                    )}
                  </>
                )}
              </NavLink>
              {item.children && !collapsed && (
                <button
                  type="button"
                  aria-label={`${item.label}のサブメニュー`}
                  aria-expanded={expanded === item.label}
                  onClick={() => setExpanded((cur) => (cur === item.label ? null : item.label))}
                  className="px-2 hover:bg-[var(--role-hover)]"
                >
                  {expanded === item.label ? '▾' : '▸'}
                </button>
              )}
            </div>
            {item.children && !collapsed && expanded === item.label && (
              <ul className="bg-[var(--role-hover)]/60">
                {item.children.map((child) => {
                  const active = childActive(
                    child.to,
                    item.children ?? [],
                    pathname,
                    search,
                    eventTypeId,
                  );
                  return (
                    <li key={child.to + child.label}>
                      <Link
                        to={child.to}
                        onClick={onNavigate}
                        title={child.label}
                        aria-current={active ? 'page' : undefined}
                        className={`flex min-w-0 items-center gap-2 border-l-4 py-2 pl-6 pr-3 hover:bg-[var(--role-hover)] ${
                          active
                            ? 'border-[var(--role-accent)] bg-[var(--role-hover)] font-semibold text-white'
                            : 'border-transparent text-wpsidebar-muted'
                        }`}
                      >
                        <span className="truncate">{child.label}</span>
                        {child.badge && (
                          <CountBadge
                            count={badges[child.badge] ?? 0}
                            label={BADGE_LABELS[child.badge]}
                            className="ml-auto"
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
